import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useContext, useState } from 'react';

import OverwatchModal from '@/components/OverwatchModal';
import CurrentUserContext from '@/Contexts/CurrentUser';
import { type AccessRegistryAction } from '@/graphql/generated/graphql';
import { combinedFormat } from '@/utils/helperFunctions';

export interface ApprovalOptions {
  validTill: string | null;
  projectId: string | null;
  additionalActions: string[];
}

interface ProjectOption {
  id: string;
  title: string;
  code: string;
}

interface ApprovePermissionModalProps {
  open: boolean;
  /** What was asked for, shown so the approver knows what they are granting. */
  requestFor?: string;
  /** Whether the requested module supports project scoping. */
  projectScoped: boolean;
  /** Project the requestor asked for, if any. */
  requestedProject?: ProjectOption | null;
  projectOptions: ProjectOption[];
  handleClose: () => void;
  onApprove: (options: ApprovalOptions) => void;
  otherActions: AccessRegistryAction[];
}

const ApprovePermissionModal: React.FC<ApprovePermissionModalProps> = ({
  open,
  requestFor,
  projectScoped,
  requestedProject,
  projectOptions,
  handleClose,
  onApprove,
  otherActions,
}) => {
  const { dateTimePreference } = useContext(CurrentUserContext);
  const [accessType, setAccessType] = useState<'permanent' | 'temporary'>('permanent');
  const [expirationDate, setExpirationDate] = useState<Dayjs | null>(dayjs().add(1, 'day'));
  const [scopeMode, setScopeMode] = useState<'requested' | 'narrow'>('requested');
  const [project, setProject] = useState<ProjectOption | null>(requestedProject ?? null);
  const [selectedActions, setSelectedActions] = useState<string[]>([]);

  if (!open) return null;

  const handleActionToggle = (actionKey: string) => {
    setSelectedActions((prev) =>
      prev.includes(actionKey) ? prev.filter((key) => key !== actionKey) : [...prev, actionKey],
    );
  };

  const handleApprove = () => {
    onApprove({
      validTill: accessType === 'temporary' && expirationDate ? expirationDate.toISOString() : null,
      projectId: scopeMode === 'narrow' ? (project?.id ?? null) : (requestedProject?.id ?? null),
      additionalActions: selectedActions,
    });
    handleClose();
  };

  return (
    <OverwatchModal
      open={open}
      title="Approve access request"
      handleClose={handleClose}
      primaryButtonText="Approve"
      primaryButtonAction={handleApprove}
      disablePrimary={scopeMode === 'narrow' && !project}
      secondaryButtonText="Cancel"
      secondaryButtonAction={handleClose}
      size="sm"
    >
      <Grid container direction="column" spacing={2}>
        {requestFor && (
          <Grid>
            <Typography variant="body2" color="text.secondary">
              Granting: <strong>{requestFor}</strong>
            </Typography>
          </Grid>
        )}

        {otherActions && otherActions.length > 0 && (
          <FormControl component="fieldset" variant="standard" fullWidth>
            <FormLabel component="legend">Additional Permissions</FormLabel>
            <FormGroup sx={{ mt: 1 }}>
              <Grid container>
                {otherActions.map((action) => {
                  const isChecked = selectedActions.includes(action.key);
                  return (
                    <Grid key={action.key} style={{ marginBottom: '8px' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isChecked}
                            onChange={() => handleActionToggle(action.key)}
                            sx={{
                              color: action.color || 'primary.main',
                              '&.Mui-checked': {
                                color: action.color || 'primary.main',
                              },
                              p: 0.5,
                              mr: 1,
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2" fontWeight={500}>
                            {action.name}
                          </Typography>
                        }
                        sx={{ m: 0, alignItems: 'center' }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </FormGroup>
          </FormControl>
        )}

        {projectScoped && (
          <Grid>
            <FormControl>
              <FormLabel>Scope</FormLabel>
              <RadioGroup value={scopeMode} onChange={(e) => setScopeMode(e.target.value as 'requested' | 'narrow')}>
                <FormControlLabel
                  value="requested"
                  control={<Radio />}
                  label={requestedProject ? `As requested (${requestedProject.title})` : 'All projects'}
                />
                <FormControlLabel value="narrow" control={<Radio />} label="Limit to one project" />
              </RadioGroup>
            </FormControl>

            {scopeMode === 'narrow' && (
              <Autocomplete
                size="small"
                options={projectOptions}
                value={project}
                getOptionLabel={(option) => `${option.code} — ${option.title}`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) => setProject(value)}
                renderInput={(params) => <TextField {...params} placeholder="Select a project" />}
                sx={{ mt: 1 }}
              />
            )}
          </Grid>
        )}

        <Grid>
          <FormControl>
            <FormLabel>Access Duration</FormLabel>
            <RadioGroup value={accessType} onChange={(e) => setAccessType(e.target.value as 'permanent' | 'temporary')}>
              <FormControlLabel value="permanent" control={<Radio />} label="Permanent" />
              <FormControlLabel value="temporary" control={<Radio />} label="Temporary" />
            </RadioGroup>
          </FormControl>
        </Grid>

        {accessType === 'temporary' && (
          <Grid>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="Expiration Date & Time"
                value={expirationDate}
                format={combinedFormat(dateTimePreference)}
                onChange={(newValue) => setExpirationDate(newValue)}
                minDateTime={dayjs()}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </LocalizationProvider>
          </Grid>
        )}
      </Grid>
    </OverwatchModal>
  );
};

export default ApprovePermissionModal;
